import React, { useState, useRef, useEffect } from 'react';
import { FaPhone } from 'react-icons/fa';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  HeroContainer,
  WavyBackground,
  ContentContainer,
  Title,
  Subtitle,
  PhoneButtonContainer,
  PhoneButton,
  ButtonText,
  PrimaryButton,
} from './HeroSectionStyles';
import { 
  TechBackground, 
  CircuitLines, 
  TechParticles, 
  HolographicScanner,
  BorderGlow,
  TechTitle,
  SectionTitle,
  TechLoadingSpinner,
  TechSubmitButton,
  TechInput,
  TechLabel,
  TechFormGroup,
  TechFormContainer
} from '../styles/TechStylesModal';
import styled, { keyframes, css } from 'styled-components';
import InfoSectionsUS from './InfoSectionsUS';
import MoreInfoButtonUS from './MoreInfoButtonUS';

// Basic animations for modal
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const modalFadeIn = keyframes`
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const modalFadeOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-20px); }
`;

// Animations for webhook feedback
const successPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(0, 255, 200, 0.7);
    background-color: rgba(0, 255, 200, 0.4);
  }
  50% {
    box-shadow: 0 0 0 20px rgba(0, 255, 200, 0);
    background-color: rgba(0, 255, 200, 0.7);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 255, 200, 0);
    background-color: rgba(0, 255, 200, 0.4);
  }
`;

const errorPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 50, 50, 0.7);
    background-color: rgba(255, 50, 50, 0.4);
  }
  50% {
    box-shadow: 0 0 0 20px rgba(255, 50, 50, 0);
    background-color: rgba(255, 50, 50, 0.7);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 50, 50, 0);
    background-color: rgba(255, 50, 50, 0.4);
  }
`;

const dataFlowAnimation = keyframes`
  0% {
    background-position: 0% 0%;
    opacity: 0.3;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    background-position: 100% 100%;
    opacity: 0.3;
  }
`;

const iconRotate = keyframes`
  0% {
    transform: rotate(0deg) scale(1);
  }
  50% {
    transform: rotate(180deg) scale(1.2);
  }
  100% {
    transform: rotate(360deg) scale(1);
  }
`;

const textReveal = keyframes`
  0% {
    clip-path: polygon(0 0, 0 0, 0 100%, 0 100%);
    opacity: 0;
  }
  100% {
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
    opacity: 1;
  }
`;

const spinAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulseGlow = keyframes`
  0% { box-shadow: 0 0 5px rgba(0, 255, 200, 0.5); }
  50% { box-shadow: 0 0 15px rgba(0, 255, 200, 0.8); }
  100% { box-shadow: 0 0 5px rgba(0, 255, 200, 0.5); }
`;

// Styles for tech modal
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: ${props => props.isClosing ? fadeOut : fadeIn} 0.3s ease-in-out;
`;

const ModalContent = styled.div`
  position: relative;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 0 30px rgba(0, 200, 255, 0.2);
  animation: ${props => props.isClosing ? modalFadeOut : modalFadeIn} 0.3s ease-in-out;
`;

const ModalBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(10, 15, 30, 0.9);
  border: 1px solid rgba(0, 200, 255, 0.3);
  z-index: 0;
`;

const ModalBody = styled.div`
  position: relative;
  z-index: 1;
  padding: 30px;
  width: 100%;
  height: 100%;
  max-height: 80vh;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalTitle = styled(TechTitle)`
  margin-bottom: 20px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  width: 30px;
  height: 30px;
  background: transparent;
  border: none;
  color: rgba(0, 200, 255, 0.8);
  font-size: 24px;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &::before, &::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 2px;
    background-color: rgba(0, 200, 255, 0.8);
    transition: all 0.3s ease;
  }
  
  &::before {
    transform: rotate(45deg);
  }
  
  &::after {
    transform: rotate(-45deg);
  }
  
  &:hover {
    transform: scale(1.1);
    
    &::before, &::after {
      background-color: rgba(0, 255, 255, 1);
      box-shadow: 0 0 8px rgba(0, 255, 255, 0.8);
    }
  }
`;

const Form = styled(TechFormContainer)`
  margin-top: 20px;
`;

const FormGroup = styled(TechFormGroup)``;

const Label = styled(TechLabel)``;

const Input = styled(TechInput)``;

const SubmitButton = styled(TechSubmitButton)``;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  margin-left: 10px;
  border: 2px solid rgba(0, 255, 200, 0.3);
  border-top: 2px solid rgba(0, 255, 200, 1);
  border-radius: 50%;
  animation: ${spinAnimation} 1s linear infinite;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    border-radius: 50%;
    border: 1px solid rgba(0, 255, 200, 0.5);
    animation: ${pulseGlow} 1.5s ease-in-out infinite;
  }
`;

const StatusMessage = styled.div`
  margin-top: 15px;
  padding: 15px;
  border-radius: 8px;
  text-align: center;
  font-family: 'Orbitron', sans-serif;
  font-size: 16px;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  background-color: ${props => props.success ? 'rgba(0, 40, 40, 0.6)' : 'rgba(40, 0, 0, 0.6)'};
  color: ${props => props.success ? 'rgba(0, 255, 200, 1)' : 'rgba(255, 50, 50, 1)'};
  border: 1px solid ${props => props.success ? 'rgba(0, 255, 200, 0.5)' : 'rgba(255, 50, 50, 0.5)'};
  box-shadow: 0 0 15px ${props => props.success ? 'rgba(0, 255, 200, 0.3)' : 'rgba(255, 50, 50, 0.3)'};
  animation: ${fadeIn} 0.3s ease-in-out;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: ${props => props.success ? 
      `linear-gradient(45deg, rgba(0, 255, 200, 0.1) 25%, transparent 25%, transparent 50%, rgba(0, 255, 200, 0.1) 50%, rgba(0, 255, 200, 0.1) 75%, transparent 75%, transparent)` : 
      `linear-gradient(45deg, rgba(255, 50, 50, 0.1) 25%, transparent 25%, transparent 50%, rgba(255, 50, 50, 0.1) 50%, rgba(255, 50, 50, 0.1) 75%, transparent 75%, transparent)`
    };
    background-size: 20px 20px;
    opacity: 0.5;
    z-index: 0;
    animation: ${dataFlowAnimation} 3s infinite linear;
  }
`;

const StatusIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
  position: relative;
  z-index: 1;
  background-color: ${props => props.success ? 'rgba(0, 255, 200, 0.2)' : 'rgba(255, 50, 50, 0.2)'};
  color: ${props => props.success ? 'rgba(0, 255, 200, 1)' : 'rgba(255, 50, 50, 1)'};
  font-size: 20px;
  animation: ${props => props.success ? successPulse : errorPulse} 2s infinite;
  
  &::before {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    border-radius: 50%;
    border: 1px solid ${props => props.success ? 'rgba(0, 255, 200, 0.5)' : 'rgba(255, 50, 50, 0.5)'};
    animation: ${iconRotate} 4s infinite linear;
  }
`;

const StatusText = styled.div`
  position: relative;
  z-index: 1;
  font-weight: 500;
  letter-spacing: 1px;
  animation: ${textReveal} 0.5s forwards;
`;

const HeroSectionUS = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [showInfoSections, setShowInfoSections] = useState(false);
  const infoSectionsRef = useRef(null);
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    empresa: '',
    segmento: ''
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ message: '', success: false, show: false });
  const navigate = useNavigate();

  // Function to format phone number
  const formatPhoneNumber = (value) => {
    // Remove all non-numeric characters
    const numbers = value.replace(/\D/g, '');
    
    // Apply formatting based on number of digits
    if (numbers.length <= 2) {
      return numbers.length ? `(${numbers}` : '';
    } else if (numbers.length <= 7) {
      return `(${numbers.substring(0, 2)}) ${numbers.substring(2)}`;
    } else {
      return `(${numbers.substring(0, 2)}) ${numbers.substring(2, 7)}-${numbers.substring(7, 11)}`;
    }
  };

  const handleOpenModal = (fromHero = true) => {
    setIsModalOpen(true);
    setIsModalClosing(false);
    // Store modal origin
    sessionStorage.setItem('modalOpenedFromHero', fromHero);
  };

  const handleCloseModal = () => {
    setIsModalClosing(true);
    // Wait for animation to finish before closing modal
    setTimeout(() => {
      setIsModalOpen(false);
      setIsModalClosing(false);
      // Reset form and status when closing modal
      setFormData({
        nome: '',
        telefone: '',
        empresa: '',
        segmento: ''
      });
      setStatus({ message: '', success: false, show: false });
      
      // Check if modal was opened from main button
      const fromHero = sessionStorage.getItem('modalOpenedFromHero') === 'true';
      
      // Scroll to top only if modal was opened from main button
      if (fromHero) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      
      // Clear storage
      sessionStorage.removeItem('modalOpenedFromHero');
    }, 300);
  };

  const handleOverlayClick = (e) => {
    // Close modal only if click was directly on overlay and not on a child element
    if (e.target === e.currentTarget) {
      handleCloseModal();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Apply specific formatting for phone field
    if (name === 'telefone') {
      setFormData(prev => ({
        ...prev,
        [name]: formatPhoneNumber(value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ message: '', success: false, show: false });

    try {
      const response = await axios.post(
        'https://webhook-editor.abianca.com.br/webhook/7c0f87b7-caa3-4378-aa98-043d3befb747',
        formData
      );
      
      if (response.data && response.data.includes('sucesso')) {
        console.log('Data successfully sent to webhook!');
        
        // Redirect to thank you page immediately
        navigate('/ty-us');
      } else {
        console.error('Webhook returned failure:', response.data);
        setStatus({
          message: 'There was an error processing your request. Please try again.',
          success: false,
          show: true
        });
        setLoading(false);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setStatus({
        message: 'There was an error processing your request. Please try again.',
        success: false,
        show: true
      });
      setLoading(false);
    }
  };

  const handleShowInfoSections = () => {
    setShowInfoSections(true);
    // Wait a bit for fade-in animation to start before scrolling
    setTimeout(() => {
      if (infoSectionsRef.current) {
        // Calculate scroll position with offset to avoid cutting top section
        const yOffset = -50;
        const y = infoSectionsRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
        
        window.scrollTo({
          top: y,
          behavior: 'smooth'
        });
      }
    }, 300);
  };

  return (
    <HeroContainer>
      <WavyBackground />
      <ContentContainer>
        <Title>
          <div className="logo-container">
            <div className="logo-tech-circle"></div>
            <div className="logo-tech-data"></div>
            <img src="/favicon.png" alt="Logo Bianca" className="title-logo" />
          </div>
          <div className="title-text-container">
            <div className="tech-data-top">// AI.AGENT</div>
            <span className="title-text">BIANCA</span>
            <div className="tech-data-bottom">{/* data.transform(leads) */}</div>
          </div>
        </Title>
        <Subtitle>
          Transforming leads into appointments
        </Subtitle>
        
        <PhoneButtonContainer>
          <div>
            <PhoneButton onClick={() => handleOpenModal(true)}>
              <FaPhone />
            </PhoneButton>
            <ButtonText>Free Trial</ButtonText>
          </div>
        </PhoneButtonContainer>
        
        {/* More Information Button */}
        <MoreInfoButtonUS onClick={handleShowInfoSections} />
        
        {/* Information sections conditionally rendered */}
        {showInfoSections && (
          <InfoSectionsUS 
            isVisible={showInfoSections} 
            setRef={(el) => (infoSectionsRef.current = el)} 
            onOpenModal={handleOpenModal}
          />
        )}
      </ContentContainer>

      {/* Modal */}
      {isModalOpen && (
        <ModalOverlay onClick={handleOverlayClick} isClosing={isModalClosing}>
          <ModalContent isClosing={isModalClosing}>
            <ModalBackground />
            
            {/* Tech background elements */}
            <TechBackground />
            <CircuitLines />
            <BorderGlow />
            <TechParticles />
            <HolographicScanner />
            
            <ModalBody>
              <ModalTitle>Get a Call</ModalTitle>
              <CloseButton onClick={handleCloseModal} />
              
              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label htmlFor="nome">Name</Label>
                  <Input 
                    type="text" 
                    id="nome" 
                    name="nome" 
                    value={formData.nome} 
                    onChange={handleChange} 
                    required 
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="telefone">Phone</Label>
                  <Input 
                    type="tel" 
                    id="telefone" 
                    name="telefone" 
                    value={formData.telefone} 
                    onChange={handleChange} 
                    required 
                    maxLength={15}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="empresa">Company Name</Label>
                  <Input 
                    type="text" 
                    id="empresa" 
                    name="empresa" 
                    value={formData.empresa} 
                    onChange={handleChange} 
                    required 
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="segmento">Industry</Label>
                  <Input 
                    type="text" 
                    id="segmento" 
                    name="segmento" 
                    value={formData.segmento} 
                    onChange={handleChange} 
                    required 
                  />
                </FormGroup>
                
                <SubmitButton type="submit" disabled={loading}>
                  {loading ? (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      PROCESSING <LoadingSpinner />
                    </span>
                  ) : (
                    'SUBMIT'
                  )}
                </SubmitButton>
                
                {status.show && (
                  <StatusMessage success={status.success}>
                    <StatusIcon success={status.success}>
                      {status.success ? '✓' : '✕'}
                    </StatusIcon>
                    <StatusText>
                      {status.message}
                    </StatusText>
                  </StatusMessage>
                )}
              </Form>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </HeroContainer>
  );
};

export default HeroSectionUS;
